import { ref, onMounted, computed, watch } from 'vue';
import { AlmacenService } from '@/services/AlmacenService';
import { hasPermission } from '@/store/userStore';
import { PERMISSIONS } from '@/constants/permissions';
import Swal from 'sweetalert2';
import { PlusIcon, TrashIcon, XIcon, Loader2Icon, TruckIcon, CheckCircleIcon, HashIcon, MapPinIcon, MapIcon, InfoIcon, SearchIcon, EyeIcon, SettingsIcon, BoxIcon } from 'lucide-vue-next';
import { ProductoService } from '@/services/ProductoService';
const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
});
export default {
    name: 'TrasladosTab',
    components: {
        PlusIcon, TrashIcon, XIcon, Loader2Icon, TruckIcon,
        CheckCircleIcon, HashIcon, MapPinIcon, MapIcon, InfoIcon, SearchIcon, EyeIcon, SettingsIcon, BoxIcon
    },
    setup() {
        const traslados = ref([]);
        const almacenes = ref([]); // needed for the dropdowns
        const isLoading = ref(false);
        const isSaving = ref(false);
        const isModalOpen = ref(false);
        const isDetailsModalOpen = ref(false);
        const selectedTraslado = ref(null);
        const filterStatus = ref('');
        const searchQuery = ref('');
        const currentPage = ref(1);
        const itemsPerPage = 8;
        const totalCount = ref(0);
        const form = ref({
            almacenOrigenId: '',
            almacenDestinoId: '',
            numeroTraslado: '',
            observaciones: '',
            detalles: []
        });
        // Stock Tracking
        const originInventory = ref(new Map());
        const fetchOriginInventory = async () => {
            if (!form.value.almacenOrigenId) {
                originInventory.value.clear();
                return;
            }
            try {
                const data = await AlmacenService.getInventario(form.value.almacenOrigenId);
                const invMap = new Map();
                data.forEach((item) => {
                    invMap.set(item.productoId, item.stockActual);
                });
                originInventory.value = invMap;
            }
            catch (error) {
                console.error("Error fetching inventory:", error);
            }
        };
        watch(() => form.value.almacenOrigenId, fetchOriginInventory);
        const productSearchTerm = ref('');
        const searchResults = ref([]);
        const fetchTraslados = async () => {
            isLoading.value = true;
            try {
                const [dataT, dataA] = await Promise.all([
                    AlmacenService.getAllTraslados(searchQuery.value, undefined, undefined, filterStatus.value || undefined, currentPage.value, itemsPerPage),
                    AlmacenService.getAllAlmacenesSimple()
                ]);
                traslados.value = dataT.items;
                totalCount.value = dataT.totalCount;
                almacenes.value = dataA;
            }
            catch (error) {
                console.error(error);
                Toast.fire({ icon: 'error', title: 'Error cargando traslados' });
            }
            finally {
                isLoading.value = false;
            }
        };
        onMounted(fetchTraslados);
        const handleSearch = () => {
            currentPage.value = 1;
            fetchTraslados();
        };
        const totalPages = computed(() => Math.ceil(totalCount.value / itemsPerPage) || 1);
        const prevPage = () => {
            if (currentPage.value > 1) {
                currentPage.value--;
                fetchTraslados();
            }
        };
        const nextPage = () => {
            if (currentPage.value < totalPages.value) {
                currentPage.value++;
                fetchTraslados();
            }
        };
        const openCreateModal = () => {
            form.value = { almacenOrigenId: '', almacenDestinoId: '', numeroTraslado: '', observaciones: '', detalles: [] };
            isModalOpen.value = true;
        };
        const closeModal = () => { isModalOpen.value = false; };
        const viewTrasladoDetails = async (id) => {
            selectedTraslado.value = null;
            isDetailsModalOpen.value = true;
            try {
                const data = await AlmacenService.getTrasladoById(id);
                selectedTraslado.value = data;
            }
            catch (error) {
                Toast.fire({ icon: 'error', title: 'Error al obtener detalles' });
                closeDetailsModal();
            }
        };
        const closeDetailsModal = () => {
            isDetailsModalOpen.value = false;
        };
        const invalidLocations = computed(() => {
            return form.value.almacenOrigenId && form.value.almacenDestinoId && form.value.almacenOrigenId === form.value.almacenDestinoId;
        });
        const validateLocations = () => {
            if (invalidLocations.value) {
                Swal.fire('Error', 'El origen y destino no pueden ser el mismo', 'warning');
            }
        };
        // --- Product Search logic ---
        let searchTimer = null;
        const searchProducts = () => {
            clearTimeout(searchTimer);
            if (!productSearchTerm.value) {
                searchResults.value = [];
                return;
            }
            searchTimer = setTimeout(async () => {
                try {
                    const res = await ProductoService.getProductosSearch(productSearchTerm.value);
                    const addedIds = new Set(form.value.detalles.map((d) => d.productoId));
                    searchResults.value = (res.datos || []).filter((p) => !addedIds.has(p.id));
                }
                catch (error) {
                    console.error(error);
                }
            }, 300);
        };
        const addProductToList = (p) => {
            if (form.value.detalles.find((d) => d.productoId === p.id)) {
                Toast.fire({ icon: 'warning', title: 'Producto ya añadido' });
                return;
            }
            form.value.detalles.push({
                productoId: p.id,
                nombreProducto: p.nombre,
                codigoUnico: p.codigoUnico,
                cantidad: 1
            });
            productSearchTerm.value = '';
            searchResults.value = [];
        };
        const removeItem = (index) => {
            form.value.detalles.splice(index, 1);
        };
        const saveTraslado = async () => {
            isSaving.value = true;
            try {
                if (form.value.detalles.length === 0) {
                    Swal.fire('Error', 'Debes añadir al menos un producto al traslado', 'warning');
                    isSaving.value = false;
                    return;
                }
                await AlmacenService.createTraslado({
                    almacenOrigenId: form.value.almacenOrigenId,
                    almacenDestinoId: form.value.almacenDestinoId,
                    numeroTraslado: form.value.numeroTraslado,
                    observaciones: form.value.observaciones,
                    detalles: form.value.detalles,
                    fechaTraslado: new Date().toISOString()
                });
                Toast.fire({ icon: 'success', title: 'Traslado registrado exitosamente.' });
                closeModal();
                fetchTraslados();
            }
            catch (error) {
                Swal.fire('Error', error.message || 'Error al registrar traslado', 'error');
            }
            finally {
                isSaving.value = false;
            }
        };
        const markAsCompleted = async (id) => {
            try {
                await AlmacenService.updateTrasladoEstado(id, { estadoTraslado: 'COMPLETADO', observaciones: '' });
                Toast.fire({ icon: 'success', title: 'Movimiento Completado' });
                fetchTraslados();
            }
            catch (error) {
                Swal.fire('Error', 'No se pudo actualizar', 'error');
            }
        };
        const confirmDelete = async (id) => {
            const result = await Swal.fire({
                title: '¿Anular traslado?',
                text: 'Esta acción no se puede deshacer',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, anular',
                cancelButtonText: 'Cancelar'
            });
            if (result.isConfirmed) {
                try {
                    await AlmacenService.deleteTraslado(id);
                    Toast.fire({ icon: 'success', title: 'Traslado anulado' });
                    fetchTraslados();
                }
                catch (error) {
                    Swal.fire('Error', 'Error al anular', 'error');
                }
            }
        };
        const getStatusClass = (status) => {
            if (status === 'COMPLETADO')
                return 'active'; // Green
            if (status === 'CANCELADO')
                return 'inactive'; // Red
            return 'bg-blue-100 text-blue-800 border bg-opacity-20 border-blue-200'; // Default pending blue
        };
        const formatDate = (val) => {
            if (!val)
                return '';
            try {
                const d = new Date(val);
                return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            }
            catch {
                return val;
            }
        };
        return {
            P: PERMISSIONS,
            can: hasPermission,
            traslados, almacenes, isLoading, isSaving, isModalOpen, form,
            productSearchTerm, searchResults,
            searchQuery, filterStatus, currentPage, totalPages, totalCount,
            handleSearch, prevPage, nextPage,
            openCreateModal, closeModal, saveTraslado, markAsCompleted, confirmDelete,
            getStatusClass, formatDate, validateLocations, invalidLocations,
            searchProducts, addProductToList, removeItem,
            isDetailsModalOpen, selectedTraslado, viewTrasladoDetails, closeDetailsModal,
            originInventory
        };
    }
};
